import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface PromptEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StylePrompts {
  watercolor: string;
  pencil: string;
  oil: string;
}

const DEFAULT_PROMPTS: StylePrompts = {
  watercolor: "Create a beautiful watercolor painting of this house with soft, flowing colors and architectural details. Show the house on a sunny summer summer day.\nIf the image is of something else, create a watercolor illustration based closely on the original image.",
  pencil: "Create a beautiful high contrast pencil drawing of this house. Use detailed architectural sketch style with fine line work.\nIf the image is of something else, create a pencil drawing based closely on the original image.",
  oil: "Create a beautiful oil painting style illustration of this house with detailed brushwork and rich colors. Show the house on a sunny summer summer day.\nIf the image is of something else, create an oil painting based closely on the original image."
};

const STORAGE_KEY = 'framemyhome_custom_prompts';

const PromptEditor: React.FC<PromptEditorProps> = ({ isOpen, onClose }) => {
  const [prompts, setPrompts] = useState<StylePrompts>(DEFAULT_PROMPTS);
  const [hasChanges, setHasChanges] = useState(false);

  // Load custom prompts from localStorage on mount
  useEffect(() => {
    const savedPrompts = localStorage.getItem(STORAGE_KEY);
    if (savedPrompts) {
      try {
        const parsed = JSON.parse(savedPrompts);
        setPrompts(parsed);
      } catch (error) {
        console.error('Error parsing saved prompts:', error);
      }
    }
  }, []);

  // Check for changes
  useEffect(() => {
    const currentPrompts = JSON.stringify(prompts);
    const defaultPrompts = JSON.stringify(DEFAULT_PROMPTS);
    setHasChanges(currentPrompts !== defaultPrompts);
  }, [prompts]);

  const handlePromptChange = (style: keyof StylePrompts, value: string) => {
    setPrompts(prev => ({
      ...prev,
      [style]: value
    }));
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('promptsUpdated', { detail: prompts }));
    onClose();
  };

  const handleReset = () => {
    setPrompts(DEFAULT_PROMPTS);
    localStorage.removeItem(STORAGE_KEY);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('promptsUpdated', { detail: DEFAULT_PROMPTS }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1000]">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit AI Prompts</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Watercolor Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Watercolor Style Prompt
              </label>
              <textarea
                value={prompts.watercolor}
                onChange={(e) => handlePromptChange('watercolor', e.target.value)}
                className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter watercolor style prompt..."
              />
            </div>

            {/* Pencil Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pencil Style Prompt
              </label>
              <textarea
                value={prompts.pencil}
                onChange={(e) => handlePromptChange('pencil', e.target.value)}
                className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter pencil style prompt..."
              />
            </div>

            {/* Oil Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Oil Style Prompt
              </label>
              <textarea
                value={prompts.oil}
                onChange={(e) => handlePromptChange('oil', e.target.value)}
                className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter oil style prompt..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset to Defaults
            </button>
            {hasChanges && (
              <span className="text-sm text-orange-600">
                * Custom prompts are active
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptEditor;

// Export utility function to get current prompts
export const getCurrentPrompts = (): StylePrompts => {
  const savedPrompts = localStorage.getItem(STORAGE_KEY);
  if (savedPrompts) {
    try {
      return JSON.parse(savedPrompts);
    } catch (error) {
      console.error('Error parsing saved prompts:', error);
    }
  }
  return DEFAULT_PROMPTS;
}; 